/* eslint-disable react/jsx-curly-newline */
import React, { useMemo, useState } from 'react';
import SaveIcon from '@material-ui/icons/Save';
import ChatIcon from '@material-ui/icons/Chat';
import CircularProgress from '@material-ui/core/CircularProgress';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { Input, Checkbox, Button } from '../../../../../_core/_components';

import * as S from './styles';
import { GuiaPagamentoMask, FASE, FASES_MAP } from '../helpers';
import { AjuizamentoDetalhes } from '../../types';
import { mascaraDDMMAAAA, mascaraNumeracaoPtBr } from '../../../../../_core/masks';
import api from '../../../../../_core/api';
import { failure, success } from '../../../../../_core/services/toast';
import { Boleto } from '../BoletosTitulos/types';

type Props = {
  ajuizamento: AjuizamentoDetalhes;
  onUpdateGuias: (guias: GuiaPagamentoMask[]) => void;
  onSave: () => void;
  guiasPagamento: GuiaPagamentoMask[];
  onRequestUpdateAjuizamento: (ajuizamento: AjuizamentoDetalhes) => void;
  boletos?: Boleto[];
};

const StatusProcesso: React.FC<Props> = ({
  ajuizamento,
  onUpdateGuias,
  onSave,
  guiasPagamento,
  onRequestUpdateAjuizamento,
  boletos,
}) => {
  const [isLoading, setLoading] = useState(false);

  const classes = S.useStyles();

  const updateGuiasPagamento = (i: number, field: string, value: any) => {
    const updatedGuiasPagamento = [...guiasPagamento];
    updatedGuiasPagamento[i] = {
      ...updatedGuiasPagamento[i],
      [field]: value,
    };
    onUpdateGuias(updatedGuiasPagamento);
  };

  const verificarFaseConcluida = (nomeFase: FASE) => {
    const idFase = FASES_MAP.get(nomeFase)!;
    const fase = ajuizamento.historicoFases.find(e => e.fase.id === idFase);
    if (fase) {
      return fase.conclusao.length > 0;
    }
    return false;
  };

  const isAbrirChamadoVisible = useMemo(() => {
    if (!boletos) return false;
    const titulosConsiderarArr = boletos.flatMap(boleto => boleto.titulos?.map(titulo => titulo.considerar) || []);
    if (titulosConsiderarArr.every(e => e === false)) return false;
    return ajuizamento.guias.find(g => g.comprovantePagamento);
  }, [ajuizamento, boletos]);

  const abrirChamado = async () => {
    try {
      setLoading(true);
      await api.post(`/workflow/ajuizamento/chamado/acoes/${ajuizamento.idAcaoAjuizamento}/abrir-chamado`);
      success('Chamado aberto com sucesso');
    } catch (e) {
      failure('Erro ao tentar abrir chamado, entre em contato com a equipe técnica');
    }
    onRequestUpdateAjuizamento(ajuizamento);
    setLoading(false);
  };

  return (
    <S.Wrapper>
      <Table className={classes.table} aria-label="customized table">
        <TableHead>
          <TableRow>
            <S.StyledTableCell>Nº Chamado Ajuizamento</S.StyledTableCell>
            <S.StyledTableCell>Status Chamado Ajuizamento</S.StyledTableCell>
            <S.StyledTableCell>Número Protocolo</S.StyledTableCell>
            <S.StyledTableCell>Data Ajuizamento</S.StyledTableCell>
            <S.StyledTableCell>Guia pgto. (R$)</S.StyledTableCell>
            <S.StyledTableCell>Num. PO</S.StyledTableCell>
            <S.StyledTableCell>Num. Chamado pgto.</S.StyledTableCell>
            <S.StyledTableCell>Comp PG (R$)</S.StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {guiasPagamento?.map((dado, idx) => (
            <S.StyledTableRow key={idx}>
              <S.StyledTableCell>
                <Input
                  disabled
                  state={[
                    dado.numeroChamadoAjuizamento,
                    value => updateGuiasPagamento(idx, 'numeroChamadoAjuizamento', value.toString()),
                  ]}
                />
              </S.StyledTableCell>
              <S.StyledTableCell>
                <Input
                  disabled
                  state={[
                    dado.statusChamadoAjuizamento,
                    option => updateGuiasPagamento(idx, 'statusChamadoAjuizamento', option),
                  ]}
                />
              </S.StyledTableCell>
              <S.StyledTableCell>
                <Input
                  disabled
                  state={[dado.numeroProtocolo, value => updateGuiasPagamento(idx, 'numeroProtocolo', value)]}
                />
              </S.StyledTableCell>
              <S.StyledTableCell>
                <Input
                  pattern={mascaraDDMMAAAA}
                  placeholder="DD/MM/YYYY"
                  state={[
                    dado.dataAjuizamento,
                    value => updateGuiasPagamento(idx, 'dataAjuizamento', value.toString()),
                  ]}
                  disabled
                />
              </S.StyledTableCell>
              <S.StyledTableCell>
                <Input
                  pattern={mascaraNumeracaoPtBr}
                  state={[dado.valor, value => updateGuiasPagamento(idx, 'valor', value.toString())]}
                  disabled={verificarFaseConcluida('Guia de Pagamento')}
                />
              </S.StyledTableCell>
              <S.StyledTableCell>
                <Input
                  state={[dado.numeroPO, value => updateGuiasPagamento(idx, 'numeroPO', value.toString())]}
                  disabled={!dado.valor || verificarFaseConcluida('PO')}
                />
              </S.StyledTableCell>
              <S.StyledTableCell>
                <Input
                  state={[
                    dado.numeroChamadoPagamento,
                    value => updateGuiasPagamento(idx, 'numeroChamadoPagamento', value.toString()),
                  ]}
                  disabled={!dado.valor || verificarFaseConcluida('Chamado para Pagamento')}
                />
              </S.StyledTableCell>
              <S.StyledTableCell>
                <Checkbox
                  checked={dado.comprovantePagamento}
                  onClick={() =>
                    updateGuiasPagamento(idx, 'comprovantePagamento', !guiasPagamento[idx].comprovantePagamento)
                  }
                  disabled={!dado.numeroChamadoPagamento || verificarFaseConcluida('Comprovantes de Pagamento')}
                />
              </S.StyledTableCell>
            </S.StyledTableRow>
          ))}
        </TableBody>
      </Table>
      <S.ActionsWrapper>
        <Button
          variant="outlined"
          onClick={() => abrirChamado()}
          startIcon={isLoading ? <CircularProgress /> : <ChatIcon />}
          text="Abrir Chamado"
        />
        <Button onClick={onSave} startIcon={<SaveIcon />} text="Salvar" />
      </S.ActionsWrapper>
    </S.Wrapper>
  );
};

export default StatusProcesso;
